import { IconType } from "@/components/Core/AppIcon";

import { PaymentMethodOption, PaymentMode } from "./type";

export const CURRENCY_OPTIONS_QUERY_KEY = ["currency-options"];
export const WALLET_OPTIONS_QUERY_KEY = ["wallet-currency-options"];
export const CRYPTO_OPTIONS_QUERY_KEY = ["crypto-currency-options"];
export const PLAYER_WALLETS_QUERY_KEY = ["player-wallets"];

const HIDE_EMPTY_WALLETS_KEY = "wallet-hide-empty";

export const readHideEmptyWallets = (): boolean => {
  try {
    return localStorage.getItem(HIDE_EMPTY_WALLETS_KEY) === "1";
  } catch {
    return false;
  }
};

export const writeHideEmptyWallets = (hide: boolean): void => {
  try {
    localStorage.setItem(HIDE_EMPTY_WALLETS_KEY, hide ? "1" : "0");
  } catch {
    // Only a convenience: with storage blocked the list just shows every wallet.
  }
};

export const KYC_IDVERIFY_STATUS_QUERY_KEY = ["kyc-idverify-status"];

export const PAYMENT_METHODS_QUERY_KEY = "payment-methods";

export const QUICK_AMOUNTS = [10, 25, 50, 100, 250, 500];

export const PAYMENT_METHODS: Record<PaymentMode, PaymentMethodOption[]> = {
  deposit: [
    { value: "card", label: "Credit / Debit Card", icon: "card" },
    { value: "bank", label: "Bank Transfer", icon: "wallet" },
  ],
  withdraw: [
    { value: "bank", label: "Bank Transfer", icon: "wallet" },
    { value: "btc", label: "Bitcoin", icon: "btc" },
    { value: "eth", label: "Ethereum", icon: "eth" },
    { value: "usdt", label: "Tether (USDT)", icon: "usdt" },
  ],
};

export const PAYMENT_METHOD_ICON_BY_CODE: Record<string, IconType> = {
  card: "card",
  bank: "wallet",
  btc: "btc",
  bitcoin: "btc",
  eth: "eth",
  ethereum: "eth",
  usdt: "usdt",
  tether: "usdt",
  usdc: "usdc",
  ltc: "ltc",
  xrp: "xrp",
};

export const CRYPTO_ASSETS_QUERY_KEY = ["crypto-assets"];

export const DEPOSIT_BONUSES_QUERY_KEY = ["deposit-bonuses"];

// Local icons for the crypto assets we happen to ship art for. The gateway
// sends an `image` URL per asset and that wins when it is set; this is only the
// fallback, and anything not listed falls back again to a monogram rather than
// to a lookalike icon - showing the USDC mark next to USDe would be worse than
// showing no mark at all.
export const CRYPTO_ASSET_ICON_BY_CODE: Record<string, IconType> = {
  USDT: "usdt",
  // Bridged USDT. A different jetton from native USDT, but the same dollar and
  // the same logo.
  JUSDT: "usdt",
  USDC: "usdc",
  // The same coin on other chains: the code names the chain, the mark stays.
  USDT_ERC20: "usdt",
  USDT_BEP20: "usdt",
  USDT_TRC20: "usdt",
  USDC_ERC20: "usdc",
  USDC_BEP20: "usdc",
  USDC_SOL: "usdc",
  BTC: "btc",
  ETH: "eth",
  LTC: "ltc",
};

// "USDT · TRON (TRC20)": the network a player must send on, next to the asset.
export const networkLabel = (asset: {
  chain?: string;
  chainName?: string;
  tokenStandard?: string | null;
  network?: string;
}): string => {
  const chain = asset.chainName || asset.chain || "TON";
  return asset.tokenStandard && asset.tokenStandard !== "jetton"
    ? `${chain} (${asset.tokenStandard.toUpperCase()})`
    : chain;
};

// What an address on the chain looks like, as a field placeholder.
export const ADDRESS_PLACEHOLDER: Record<string, string> = {
  ton: "UQ…",
  ethereum: "0x…",
  bsc: "0x…",
  tron: "T…",
  bitcoin: "bc1…",
  litecoin: "ltc1…",
  solana: "…",
};

// Smallest-unit integer -> human string, trailing zeros trimmed.
//
// The gateway's limits are raw integers in the asset's own unit (1 USDT is
// 1000000, 1 TON is 1000000000), so they cannot be shown to a player without
// this. Decimals come from the asset, never assumed - USD₮ on TON has 6, not 9.
export const formatAssetRaw = (raw: string | undefined, decimals: number): string => {
  if (!raw) return "0";

  const digits = raw.padStart(decimals + 1, "0");
  const whole = digits.slice(0, digits.length - decimals);
  const fraction = decimals > 0 ? digits.slice(digits.length - decimals) : "";
  const trimmed = fraction.replace(/0+$/, "");

  return trimmed ? `${whole}.${trimmed}` : whole;
};
